import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, TrendingUp, TrendingDown, AlertTriangle, Star, Target, DollarSign, RefreshCw, Loader2, ChefHat, Grid3X3, BarChart3, X, FlaskConical } from 'lucide-react';
import { PreparationsTab } from '../components/PreparationsTab';

const CATEGORIES = ['Poke Bowl', 'Bubble Tea', 'Mochi', 'Accompagnement', 'Boisson', 'Dessert', 'Formule', 'Autre'];

export function RentabilitePage() {
  const { api } = useAuth();
  const [plats, setPlats] = useState([]);
  const [analyse, setAnalyse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ nom: '', categorie: 'Poke Bowl', prix_vente: '', ingredients: [], nb_vendus: '' });
  const [newIng, setNewIng] = useState({ nom: '', quantite: '', unite: 'g', cout_unitaire: '' });
  const [mercurialeItems, setMercurialeItems] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, aRes] = await Promise.all([
        api.get('/rentabilite/plats'),
        api.get('/rentabilite/analyse'),
      ]);
      setPlats(pRes.data);
      setAnalyse(aRes.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch mercuriale items for autocomplete
  useEffect(() => {
    api.get('/mercuriale/produits').then(res => setMercurialeItems(res.data)).catch(() => {});
  }, [api]);

  const openCreate = () => {
    setEditItem(null);
    setForm({ nom: '', categorie: 'Poke Bowl', prix_vente: '', ingredients: [], nb_vendus: '' });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditItem(p);
    setForm({ nom: p.nom, categorie: p.categorie, prix_vente: p.prix_vente.toString(), ingredients: [...p.ingredients], nb_vendus: (p.nb_vendus || '').toString() });
    setDialogOpen(true);
  };

  const addIngredient = () => {
    if (!newIng.nom.trim()) return;
    setForm(f => ({ ...f, ingredients: [...f.ingredients, { ...newIng, quantite: parseFloat(newIng.quantite) || 0, cout_unitaire: parseFloat(newIng.cout_unitaire) || 0 }] }));
    setNewIng({ nom: '', quantite: '', unite: 'g', cout_unitaire: '' });
  };

  const removeIngredient = (idx) => {
    setForm(f => ({ ...f, ingredients: f.ingredients.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    if (!form.nom.trim() || !form.prix_vente) { toast.error('Nom et prix requis'); return; }
    const payload = { ...form, prix_vente: parseFloat(form.prix_vente), nb_vendus: parseInt(form.nb_vendus) || 0 };
    try {
      if (editItem) {
        await api.put(`/rentabilite/plats/${editItem.id}`, payload);
        toast.success('Plat mis a jour');
      } else {
        await api.post('/rentabilite/plats', payload);
        toast.success('Plat ajoute');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce plat ?')) return;
    try {
      await api.delete(`/rentabilite/plats/${id}`);
      toast.success('Supprime');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const handleSyncUber = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/rentabilite/sync-uber');
      toast.success(`${res.data.updated} plat(s) mis a jour depuis Uber Eats`);
      fetchData();
    } catch { toast.error('Erreur sync'); }
    finally { setSyncing(false); }
  };

  const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);
  const coutFromForm = form.ingredients.reduce((s, i) => s + (i.quantite || 0) * (i.cout_unitaire || 0), 0);
  const margeFromForm = (parseFloat(form.prix_vente) || 0) - coutFromForm;
  const margePctForm = (parseFloat(form.prix_vente) || 0) > 0 ? (margeFromForm / parseFloat(form.prix_vente) * 100) : 0;
  const a = analyse || {};

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6" data-testid="rentabilite-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ChefHat className="w-6 h-6" /> Rentabilite par Plat</h1>
          <p className="text-muted-foreground text-sm">Cout matiere, marge, et matrice BCG</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSyncUber} disabled={syncing} data-testid="sync-uber-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} /> Sync Uber
          </Button>
          <Button onClick={openCreate} data-testid="add-plat-btn"><Plus className="w-4 h-4 mr-2" /> Nouveau plat</Button>
        </div>
      </div>

      <Tabs defaultValue="plats">
        <TabsList>
          <TabsTrigger value="plats" data-testid="tab-plats"><ChefHat className="w-4 h-4 mr-1" /> Plats ({plats.length})</TabsTrigger>
          <TabsTrigger value="preparations" data-testid="tab-preparations"><FlaskConical className="w-4 h-4 mr-1" /> Préparations</TabsTrigger>
          <TabsTrigger value="matrice" data-testid="tab-matrice"><Grid3X3 className="w-4 h-4 mr-1" /> Matrice BCG</TabsTrigger>
          <TabsTrigger value="analyse" data-testid="tab-analyse"><BarChart3 className="w-4 h-4 mr-1" /> Analyse</TabsTrigger>
        </TabsList>

        {/* PLATS TAB */}
        <TabsContent value="plats" className="space-y-4">
          {plats.length === 0 ? (
            <Card><CardContent className="py-16 text-center">
              <ChefHat className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Aucun plat</h3>
              <p className="text-muted-foreground mb-4">Ajoutez vos plats avec leurs ingredients pour analyser la rentabilite</p>
              <Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" /> Ajouter un plat</Button>
            </CardContent></Card>
          ) : (
            <Card><CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plat</TableHead>
                    <TableHead>Categorie</TableHead>
                    <TableHead className="text-right">Prix vente</TableHead>
                    <TableHead className="text-right">Cout matiere</TableHead>
                    <TableHead className="text-right">Marge</TableHead>
                    <TableHead className="text-right">Marge %</TableHead>
                    <TableHead className="text-right">Vendus</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plats.map(p => {
                    const foodCost = p.prix_vente > 0 ? (p.cout_total / p.prix_vente * 100) : 0;
                    const updateField = async (field, val) => {
                      try {
                        await api.put(`/rentabilite/plats/${p.id}`, { [field]: val });
                        fetchPlats();
                      } catch { toast.error('Erreur sauvegarde'); }
                    };
                    return (
                      <TableRow key={p.id} data-testid={`plat-row-${p.id}`}>
                        <TableCell className="font-semibold">{p.nom}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{p.categorie}</Badge></TableCell>
                        <TableCell className="text-right tabular-nums">
                          <Input type="number" step="0.01" defaultValue={p.prix_vente}
                            className="h-8 w-24 text-right tabular-nums ml-auto"
                            onBlur={e => {
                              const v = parseFloat(e.target.value) || 0;
                              if (v !== p.prix_vente) updateField('prix_vente', v);
                            }}
                            data-testid={`plat-prix-${p.id}`} />
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          <span className={foodCost > 35 ? 'text-red-600 font-medium' : ''}>{fmt(p.cout_total)}</span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium">
                          <span className={p.marge < 0 ? 'text-red-600' : 'text-green-600'}>{fmt(p.marge)}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge className={`tabular-nums ${p.marge_pct >= 65 ? 'bg-green-100 text-green-700' : p.marge_pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                            {p.marge_pct.toFixed(0)}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Input type="number" step="1" defaultValue={p.nb_vendus || 0}
                            className="h-8 w-20 text-right tabular-nums ml-auto"
                            onBlur={e => {
                              const v = parseInt(e.target.value) || 0;
                              if (v !== (p.nb_vendus || 0)) updateField('nb_vendus', v);
                            }}
                            data-testid={`plat-vendus-${p.id}`} />
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(p)}><Pencil className="w-3 h-3" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(p.id)}><Trash2 className="w-3 h-3" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </TabsContent>

        {/* PREPARATIONS TAB */}
        <TabsContent value="preparations" className="space-y-4">
          <PreparationsTab />
        </TabsContent>

        {/* MATRICE BCG TAB */}
        <TabsContent value="matrice" className="space-y-4">
          {a.matrice ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MatriceQuadrant title="Stars" subtitle="Populaires & Rentables" icon={Star} items={a.matrice.stars} color="border-green-400 bg-green-50/50" textColor="text-green-700" badge="bg-green-100 text-green-700" advice="Gardez-les ! Ce sont vos meilleurs plats." />
              <MatriceQuadrant title="Vaches a lait" subtitle="Populaires mais marge faible" icon={DollarSign} items={a.matrice.vaches} color="border-amber-400 bg-amber-50/50" textColor="text-amber-700" badge="bg-amber-100 text-amber-700" advice="Augmentez les prix ou reduisez les couts." />
              <MatriceQuadrant title="Enigmes" subtitle="Rentables mais peu vendus" icon={Target} items={a.matrice.enigmes} color="border-blue-400 bg-blue-50/50" textColor="text-blue-700" badge="bg-blue-100 text-blue-700" advice="Mettez-les en avant ! Promotions, mise en avant menu." />
              <MatriceQuadrant title="Poids morts" subtitle="Peu vendus & marge faible" icon={TrendingDown} items={a.matrice.poids_morts} color="border-red-400 bg-red-50/50" textColor="text-red-700" badge="bg-red-100 text-red-700" advice="A revoir : modifier la recette, le prix, ou retirer du menu." />
            </div>
          ) : (
            <Card><CardContent className="py-12 text-center text-muted-foreground">Ajoutez des plats pour voir la matrice</CardContent></Card>
          )}
        </TabsContent>

        {/* ANALYSE TAB */}
        <TabsContent value="analyse" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Plats" value={a.total_plats || 0} icon={ChefHat} color="text-blue-600" />
            <StatCard label="Marge moyenne" value={`${a.marge_moyenne || 0}%`} icon={TrendingUp} color={a.marge_moyenne >= 60 ? 'text-green-600' : 'text-amber-600'} />
            <StatCard label="Cout moyen" value={fmt(a.cout_moyen)} icon={DollarSign} color="text-orange-600" />
            <StatCard label="Food cost moyen" value={`${a.ratio_food_cost_moyen || 0}%`} icon={Target} color={a.ratio_food_cost_moyen <= 30 ? 'text-green-600' : 'text-red-600'} />
          </div>

          {a.meilleure_marge && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-green-200 bg-green-50/30">
                <CardContent className="pt-5 pb-4">
                  <p className="text-xs text-muted-foreground mb-1">Meilleure marge</p>
                  <p className="font-bold text-green-700">{a.meilleure_marge.nom}</p>
                  <p className="text-sm text-green-600">{a.meilleure_marge.marge_pct}% — {fmt(a.meilleure_marge.marge)}/plat</p>
                </CardContent>
              </Card>
              <Card className="border-red-200 bg-red-50/30">
                <CardContent className="pt-5 pb-4">
                  <p className="text-xs text-muted-foreground mb-1">Plus faible marge</p>
                  <p className="font-bold text-red-700">{a.pire_marge?.nom}</p>
                  <p className="text-sm text-red-600">{a.pire_marge?.marge_pct}% — {fmt(a.pire_marge?.marge)}/plat</p>
                </CardContent>
              </Card>
              {a.plus_vendu && (
                <Card className="border-blue-200 bg-blue-50/30">
                  <CardContent className="pt-5 pb-4">
                    <p className="text-xs text-muted-foreground mb-1">Plus vendu</p>
                    <p className="font-bold text-blue-700">{a.plus_vendu.nom}</p>
                    <p className="text-sm text-blue-600">{a.plus_vendu.nb_vendus} vendus — marge {a.plus_vendu.marge_pct}%</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {a.alertes?.length > 0 && (
            <Card data-testid="alertes-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Alertes ({a.alertes.length})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {a.alertes.map((al, i) => (
                  <div key={i} className={`flex items-start gap-2 p-2 rounded-lg text-sm ${al.type === 'danger' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div><span className="font-semibold">{al.plat}</span> — {al.message}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* CREATE/EDIT DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="plat-dialog">
          <DialogHeader><DialogTitle>{editItem ? 'Modifier' : 'Nouveau'} plat</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1 col-span-2">
                <Label>Nom du plat *</Label>
                <Input value={form.nom} onChange={e => setForm(f => ({...f, nom: e.target.value}))} placeholder="Ex: Poke Saumon" data-testid="plat-nom" />
              </div>
              <div className="space-y-1">
                <Label>Categorie</Label>
                <Select value={form.categorie} onValueChange={v => setForm(f => ({...f, categorie: v}))}>
                  <SelectTrigger data-testid="plat-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Prix de vente TTC *</Label>
                <Input type="number" step="0.01" value={form.prix_vente} onChange={e => setForm(f => ({...f, prix_vente: e.target.value}))} placeholder="13.90" data-testid="plat-prix" />
              </div>
              <div className="space-y-1">
                <Label>Nb vendus (Uber/mois)</Label>
                <Input type="number" value={form.nb_vendus} onChange={e => setForm(f => ({...f, nb_vendus: e.target.value}))} placeholder="0" data-testid="plat-vendus" />
              </div>
            </div>

            <Separator />

            {/* Ingredients */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Ingredients & couts</Label>
              {form.ingredients.length > 0 && (
                <div className="space-y-1">
                  {form.ingredients.map((ing, i) => {
                    const updateIng = (field, val) => {
                      setForm(f => ({
                        ...f,
                        ingredients: f.ingredients.map((it, idx) => idx === i ? { ...it, [field]: val } : it),
                      }));
                    };
                    return (
                      <div key={i} className="flex items-center gap-2 text-sm bg-muted/30 px-3 py-1.5 rounded-lg" data-testid={`ing-row-${i}`}>
                        <Input
                          className="flex-1 h-7 text-sm bg-background"
                          value={ing.nom}
                          onChange={e => updateIng('nom', e.target.value)}
                        />
                        <Input
                          type="number"
                          step={ing.unite === 'g' || ing.unite === 'mL' ? '1' : '0.001'}
                          className="w-20 h-7 text-sm tabular-nums bg-background"
                          value={ing.quantite}
                          onChange={e => updateIng('quantite', parseFloat(e.target.value) || 0)}
                        />
                        <Select value={ing.unite} onValueChange={v => updateIng('unite', v)}>
                          <SelectTrigger className="w-20 h-7 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['g', 'mL', 'kg', 'L', 'cl', 'unite', 'portion', 'sachet', 'piece'].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          step="0.0001"
                          className="w-24 h-7 text-sm tabular-nums bg-background"
                          value={ing.cout_unitaire}
                          onChange={e => updateIng('cout_unitaire', parseFloat(e.target.value) || 0)}
                        />
                        <span className="tabular-nums font-medium text-primary w-20 text-right">
                          = {fmt(ing.quantite * ing.cout_unitaire)}
                        </span>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeIngredient(i)}><X className="w-3 h-3" /></Button>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1 relative">
                  <Label className="text-xs">Ingredient (cherche dans Mercuriale + Préparations)</Label>
                  <Input value={newIng.nom} onChange={e => {
                    const val = e.target.value;
                    setNewIng(n => ({...n, nom: val}));
                    if (val.length >= 1) {
                      const filtered = mercurialeItems
                        .filter(m => m.nom.toLowerCase().includes(val.toLowerCase()))
                        .sort((a, b) => {
                          // Préparations maison en premier
                          const aPrep = a.fournisseur === 'Préparation maison' ? 0 : 1;
                          const bPrep = b.fournisseur === 'Préparation maison' ? 0 : 1;
                          if (aPrep !== bPrep) return aPrep - bPrep;
                          return a.nom.localeCompare(b.nom);
                        })
                        .slice(0, 8);
                      setSuggestions(filtered);
                      setShowSuggestions(filtered.length > 0);
                    } else {
                      // Affiche TOUTES les préparations maison quand vide
                      const preps = mercurialeItems
                        .filter(m => m.fournisseur === 'Préparation maison')
                        .sort((a, b) => a.nom.localeCompare(b.nom));
                      setSuggestions(preps);
                      setShowSuggestions(preps.length > 0);
                    }
                  }}
                  onFocus={() => {
                    if (newIng.nom.length === 0) {
                      const preps = mercurialeItems
                        .filter(m => m.fournisseur === 'Préparation maison')
                        .sort((a, b) => a.nom.localeCompare(b.nom));
                      setSuggestions(preps);
                      setShowSuggestions(preps.length > 0);
                    } else {
                      setShowSuggestions(suggestions.length > 0);
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  placeholder="Saumon, Riz, Panang..." data-testid="ing-nom" />
                  {showSuggestions && (
                    <div className="absolute z-50 top-full mt-1 w-full bg-white dark:bg-card border rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid="ing-suggestions">
                      {suggestions.map(s => {
                        const isPrep = s.fournisseur === 'Préparation maison';
                        return (
                          <button key={s.id} type="button"
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-primary/5 flex items-center justify-between border-b last:border-0 ${isPrep ? 'bg-blue-50/40 dark:bg-blue-950/20' : ''}`}
                            onMouseDown={() => {
                              const u = (s.unite || '').toLowerCase().trim();
                              const isWeight = ['kg', 'g'].includes(u);
                              const isVolume = ['l', 'cl', 'ml'].includes(u);
                              if (isPrep) {
                                // Préparation : 1 portion à coût unitaire
                                setNewIng({
                                  nom: s.nom,
                                  quantite: '1',
                                  unite: s.unite || 'portion',
                                  cout_unitaire: (s.prix_unitaire || 0).toString(),
                                });
                              } else if (isWeight && s.prix_au_g) {
                                // Solide : passe en gramme avec prix au gramme
                                setNewIng({
                                  nom: s.nom,
                                  quantite: '',
                                  unite: 'g',
                                  cout_unitaire: s.prix_au_g.toString(),
                                });
                              } else if (isVolume && s.prix_au_g) {
                                // Liquide : passe en mL avec prix au mL
                                setNewIng({
                                  nom: s.nom,
                                  quantite: '',
                                  unite: 'mL',
                                  cout_unitaire: s.prix_au_g.toString(),
                                });
                              } else {
                                // Pièce / sachet : prix unitaire
                                setNewIng({
                                  nom: s.nom,
                                  quantite: '',
                                  unite: s.unite || 'unite',
                                  cout_unitaire: (s.prix_unitaire || 0).toString(),
                                });
                              }
                              setShowSuggestions(false);
                            }}>
                            <span className="font-medium flex items-center gap-2">
                              {s.nom}
                              {isPrep && <Badge className="text-[10px] py-0 h-4 bg-blue-600 text-white"><FlaskConical className="w-2.5 h-2.5 mr-0.5" />Préparation</Badge>}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {(() => {
                                const u = (s.unite || '').toLowerCase().trim();
                                const isWeight = ['kg', 'g'].includes(u);
                                const isVolume = ['l', 'cl', 'ml'].includes(u);
                                if (!isPrep && isWeight && s.prix_au_g) {
                                  return `${s.prix_au_g.toFixed(4)} EUR/g`;
                                }
                                if (!isPrep && isVolume && s.prix_au_g) {
                                  return `${s.prix_au_g.toFixed(4)} EUR/mL`;
                                }
                                return `${(s.prix_unitaire || 0).toFixed(s.prix_unitaire < 1 ? 4 : 2)} EUR/${s.unite}`;
                              })()}
                              {!isPrep && s.fournisseur ? ` · ${s.fournisseur}` : ''}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="w-20 space-y-1">
                  <Label className="text-xs">Quantite</Label>
                  <Input type="number" step={newIng.unite === 'g' || newIng.unite === 'mL' ? '1' : '0.001'} value={newIng.quantite} onChange={e => setNewIng(n => ({...n, quantite: e.target.value}))} placeholder={newIng.unite === 'g' ? '150' : newIng.unite === 'mL' ? '100' : newIng.unite === 'portion' ? '1' : '0.15'} />
                </div>
                <div className="w-20 space-y-1">
                  <Label className="text-xs">Unite</Label>
                  <Select value={newIng.unite} onValueChange={v => setNewIng(n => ({...n, unite: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['g', 'mL', 'kg', 'L', 'cl', 'unite', 'portion', 'sachet', 'piece'].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-28 space-y-1">
                  <Label className="text-xs">Cout / {newIng.unite}</Label>
                  <Input type="number" step="0.0001" value={newIng.cout_unitaire} onChange={e => setNewIng(n => ({...n, cout_unitaire: e.target.value}))} placeholder={newIng.unite === 'g' ? '0.023' : newIng.unite === 'mL' ? '0.0035' : '18.00'} />
                </div>
                <Button variant="outline" onClick={addIngredient} data-testid="add-ing-btn"><Plus className="w-4 h-4" /></Button>
              </div>
              <p className="text-xs text-muted-foreground">Tapez un nom pour chercher dans la mercuriale. Si le produit n'existe pas, il sera ajoute automatiquement.</p>
            </div>

            <Separator />

            {/* Live preview */}
            <div className="grid grid-cols-3 gap-3 p-3 bg-muted/30 rounded-lg">
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Cout matiere</p>
                <p className="text-lg font-bold tabular-nums text-orange-600">{fmt(coutFromForm)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Marge</p>
                <p className={`text-lg font-bold tabular-nums ${margeFromForm >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(margeFromForm)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Marge %</p>
                <p className={`text-lg font-bold tabular-nums ${margePctForm >= 65 ? 'text-green-600' : margePctForm >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{margePctForm.toFixed(0)}%</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Annuler</Button></DialogClose>
            <Button onClick={handleSave} data-testid="save-plat-btn">{editItem ? 'Enregistrer' : 'Ajouter'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MatriceQuadrant({ title, subtitle, icon: Icon, items, color, textColor, badge, advice }) {
  return (
    <Card className={`border-2 ${color}`} data-testid={`matrice-${title.toLowerCase().replace(/ /g, '-')}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className={`text-base flex items-center gap-2 ${textColor}`}>
            <Icon className="w-5 h-5" /> {title}
          </CardTitle>
          <Badge className={badge}>{items.length}</Badge>
        </div>
        <CardDescription>{subtitle}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">Aucun plat</p>
        ) : (
          items.map(p => (
            <div key={p.id} className="flex items-center justify-between text-sm p-2 bg-white/60 rounded-lg border">
              <div>
                <span className="font-medium">{p.nom}</span>
                <span className="text-xs text-muted-foreground ml-2">{p.categorie}</span>
              </div>
              <div className="flex items-center gap-3 text-xs tabular-nums">
                <span className={p.marge_pct >= 60 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>{p.marge_pct.toFixed(0)}%</span>
                <span className="text-muted-foreground">{p.nb_vendus || 0}x</span>
              </div>
            </div>
          ))
        )}
        <p className="text-xs text-muted-foreground italic mt-2">{advice}</p>
      </CardContent>
    </Card>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <Card><CardContent className="pt-5 pb-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-muted-foreground">{label}</span>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <p className={`text-2xl font-bold tabular-nums ${color}`}>{value}</p>
    </CardContent></Card>
  );
}
